import { isPostHogLoaded, posthog } from '@lib/posthog.lib';

export type PostHogIdentifyProperties = {
  email?: string | null;
};

const resolveEmailProperty = (
  properties?: PostHogIdentifyProperties
): string | null => {
  if (properties?.email == null || properties.email === '') {
    return null;
  }

  return properties.email;
};

/**
 * Ties the current PostHog identity to the authenticated user so events,
 * session replays, and exceptions are attributed to the right person.
 *
 * Reset first when the distinct id changes: sessions that end without an
 * explicit logout can leave a persisted identity that would otherwise
 * attribute the new session to the wrong user.
 *
 * Pass the session user's email (person at the keyboard), never an
 * impersonated target's email. Always send `email` (string or null) so a
 * missing value clears a stale person property from a prior identify.
 */
export const identifyPostHogUser = (
  userId?: string | number,
  properties?: PostHogIdentifyProperties
): void => {
  if (!isPostHogLoaded() || userId == null) {
    return;
  }

  const distinctId = String(userId);
  const personProperties = { email: resolveEmailProperty(properties) };

  if (posthog.get_distinct_id() !== distinctId) {
    posthog.reset();
  }

  posthog.identify(distinctId, personProperties);
};

export const resetPostHogIdentity = (): void => {
  if (!isPostHogLoaded()) {
    return;
  }

  posthog.reset();
};

/**
 * Aligns PostHog with the current auth profile after the SDK becomes ready.
 * Identifies when a user id is present; otherwise clears any stale identity.
 */
export const syncPostHogIdentity = (
  userId?: string | number | null,
  email?: string | null
): void => {
  if (userId == null) {
    resetPostHogIdentity();

    return;
  }

  identifyPostHogUser(userId, { email: email ?? null });
};
